import { cloneElement, isValidElement, ReactNode, useContext, useMemo } from "react";

import { ProfileContext } from "@/contexts/profile";
import { PractitionerPermissionFlags } from "@/permission/constants/practitioner-permissions";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { hasRequiredPermissions } from "@/permission/utils/practitioner-permissions";

interface PermissionGateProps {
  children: ReactNode;
  requiredPermissions: PractitionerPermissionFlags[];
  fallback?: ReactNode;
  mode?: "hide" | "disable";
  /**
   * When false, patient profiles do not satisfy the gate (staff-only screens).
   * Defaults to true for backward compatibility.
   */
  allowPatient?: boolean;
}

const PermissionGate = ({
  children,
  requiredPermissions,
  fallback = null,
  mode = "hide",
  allowPatient = true,
}: PermissionGateProps) => {
  const { permissions } = usePractitionerPermissions();
  const { isPatient } = useContext(ProfileContext);

  const hasStaffRoleInRequiredList = useMemo(
    () => hasRequiredPermissions(permissions, requiredPermissions),
    [permissions, requiredPermissions],
  );

  const hasPermission = useMemo(() => {
    if (isPatient) {
      return allowPatient;
    }
    return hasStaffRoleInRequiredList;
  }, [allowPatient, isPatient, hasStaffRoleInRequiredList]);

  if (!hasPermission && mode === "hide") {
    return <>{fallback}</>;
  }

  if (!hasPermission && mode === "disable" && isValidElement(children)) {
    return cloneElement(children, {
      ...children.props,
      disabled: true,
    });
  }

  return <>{children}</>;
};

export default PermissionGate;
